// src/pages/MessageThread.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Paperclip,
  MoreVertical,
  Phone,
  Video,
  Smile,
  Image,
  Calendar,
  Star,
  Trash2,
  Archive,
} from 'lucide-react';

const MessageThread = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [thread, setThread] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    loadThread();
    markAsRead();
  }, [messageId]);

  useEffect(() => {
    scrollToBottom();
  }, [thread]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const markAsRead = () => {
    const allMessages = JSON.parse(localStorage.getItem('userMessages') || '[]');
    const updatedMessages = allMessages.map(msg => {
      if (msg.id === messageId) {
        return { ...msg, read: true };
      }
      return msg;
    });
    localStorage.setItem('userMessages', JSON.stringify(updatedMessages));
  };

  const loadThread = () => {
    setLoading(true);
    setTimeout(() => {
      const userEmail = localStorage.getItem('userEmail') || 'user@example.com';
      const allMessages = JSON.parse(localStorage.getItem('userMessages') || '[]');
      const message = allMessages.find(m => m.id === messageId);
      
      if (message) {
        // Determine the other user
        const isRecipient = message.recipient === userEmail;
        const otherUserInfo = {
          email: isRecipient ? message.sender : message.recipient,
          name: isRecipient ? message.senderName : message.recipientName,
          role: isRecipient ? message.senderRole : message.recipientRole,
          avatar: getAvatarForRole(isRecipient ? message.senderRole : message.recipientRole),
          status: 'online' // Mock status
        };
        setOtherUser(otherUserInfo);

        // Create thread from the message
        const threadMessages = [
          {
            id: message.id,
            sender: message.sender,
            senderName: message.senderName,
            content: message.fullMessage,
            timestamp: message.timestamp,
            read: true
          }
        ];

        // Add some mock conversation history
        if (message.subject.includes('Re:')) {
          threadMessages.unshift({
            id: 'prev-1',
            sender: userEmail,
            senderName: 'You',
            content: "Hi, I'm interested in scheduling a session with you. Do you have any availability this week?",
            timestamp: new Date(Date.parse(message.timestamp) - 24 * 60 * 60 * 1000).toISOString(),
            read: true
          });
        }

        // Load any saved thread messages
        const savedThreads = JSON.parse(localStorage.getItem('messageThreads') || '{}');
        if (savedThreads[messageId]) {
          threadMessages.push(...savedThreads[messageId]);
        }

        setThread(threadMessages);
      }
      setLoading(false);
    }, 300);
  };

  const getAvatarForRole = (role) => {
    const avatars = {
      practitioner: '🧘',
      client: '🔮'
    };
    return avatars[role] || '👤';
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const userEmail = localStorage.getItem('userEmail') || 'user@example.com';
    const userName = localStorage.getItem('userName') || 'You';
    
    const message = {
      id: `msg-${Date.now()}`,
      sender: userEmail,
      senderName: userName,
      content: newMessage,
      timestamp: new Date().toISOString(),
      read: false
    };

    // Add to thread
    const updatedThread = [...thread, message];
    setThread(updatedThread);

    // Save to localStorage
    const savedThreads = JSON.parse(localStorage.getItem('messageThreads') || '{}');
    if (!savedThreads[messageId]) {
      savedThreads[messageId] = [];
    }
    savedThreads[messageId].push(message);
    localStorage.setItem('messageThreads', JSON.stringify(savedThreads));

    // Clear input
    setNewMessage('');
    
    // Simulate typing indicator
    setTimeout(() => {
      setTyping(true);
      setTimeout(() => {
        simulateReply();
        setTyping(false);
      }, 2000);
    }, 1000);
  };

  const simulateReply = () => {
    if (!otherUser) return;
    
    const replies = [
      "That sounds great! I'll check my calendar and get back to you shortly.",
      "Thank you for your message. I'd be happy to help with that.",
      "Absolutely! Let's schedule a time that works for both of us.",
      "I appreciate you reaching out. Looking forward to our session!",
      "Great question! Let me provide you with some more details."
    ];

    const reply = {
      id: `msg-${Date.now()}`,
      sender: otherUser.email,
      senderName: otherUser.name,
      content: replies[Math.floor(Math.random() * replies.length)],
      timestamp: new Date().toISOString(),
      read: true
    };

    setThread(prev => [...prev, reply]);
    
    // Save to localStorage
    const savedThreads = JSON.parse(localStorage.getItem('messageThreads') || '{}');
    if (!savedThreads[messageId]) {
      savedThreads[messageId] = [];
    }
    savedThreads[messageId].push(reply);
    localStorage.setItem('messageThreads', JSON.stringify(savedThreads));
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: diffDays > 365 ? 'numeric' : undefined,
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleScheduleMeeting = () => {
    // Navigate to practitioner's booking page
    if (otherUser && otherUser.role === 'practitioner') {
      navigate(`/practitioners/${otherUser.email.split('@')[0]}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading conversation...</p>
        </div>
      </div>
    );
  }

  const userEmail = localStorage.getItem('userEmail') || 'user@example.com';

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="max-w-4xl mx-auto h-[calc(100vh-5rem)]">
        <div className="h-full bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
          {/* Header */}
          <div className="border-b border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/messages')}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                
                {otherUser && (
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl">
                        {otherUser.avatar}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                        otherUser.status === 'online' ? 'bg-green-500' : 'bg-slate-400'
                      }`} />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{otherUser.name}</div>
                      <div className="text-sm text-slate-600 capitalize">
                        {otherUser.role} • {otherUser.status}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {otherUser?.role === 'practitioner' && (
                  <>
                    <button
                      onClick={handleScheduleMeeting}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Schedule Session"
                    >
                      <Calendar className="w-5 h-5 text-slate-600" />
                    </button>
                    <button
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Voice Call"
                    >
                      <Phone className="w-5 h-5 text-slate-600" />
                    </button>
                    <button
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Video Call"
                    >
                      <Video className="w-5 h-5 text-slate-600" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors relative"
                >
                  <MoreVertical className="w-5 h-5 text-slate-600" />
                  
                  {showActions && (
                    <div className="absolute right-0 top-12 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-10">
                      <button className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                        <Star className="w-4 h-4" />
                        <span>Star conversation</span>
                      </button>
                      <button className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2">
                        <Archive className="w-4 h-4" />
                        <span>Archive</span>
                      </button>
                      <button className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center space-x-2 text-red-600">
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {thread.map((message, index) => {
              const isCurrentUser = message.sender === userEmail;
              const showTimestamp = index === 0 || 
                new Date(message.timestamp).toDateString() !== 
                new Date(thread[index - 1].timestamp).toDateString();

              return (
                <div key={message.id}>
                  {showTimestamp && (
                    <div className="text-center text-xs text-slate-500 my-4">
                      {new Date(message.timestamp).toDateString() === new Date().toDateString() 
                        ? 'Today' 
                        : new Date(message.timestamp).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                    </div>
                  )}
                  
                  <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${isCurrentUser ? 'order-2' : 'order-1'}`}>
                      <div className={`rounded-2xl px-4 py-2 ${
                        isCurrentUser 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-100 text-slate-900'
                      }`}>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                      <div className={`text-xs text-slate-500 mt-1 ${
                        isCurrentUser ? 'text-right' : 'text-left'
                      }`}>
                        {formatTimestamp(message.timestamp)}
                        {isCurrentUser && message.read && ' • Read'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {typing && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" 
                         style={{animationDelay: '0ms'}} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" 
                         style={{animationDelay: '150ms'}} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" 
                         style={{animationDelay: '300ms'}} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-4">
            <div className="flex items-end space-x-2">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <Paperclip className="w-5 h-5 text-slate-600" />
              </button>
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <Image className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type a message..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="1"
                  style={{
                    minHeight: '40px',
                    maxHeight: '120px'
                  }}
                />
              </div>
              
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <Smile className="w-5 h-5 text-slate-600" />
              </button>
              
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className={`p-2 rounded-lg transition-colors ${
                  newMessage.trim() 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;